import type { ReactNode } from 'react';
import React, { useCallback, useEffect, useState } from 'react';

function notInContextFn() {
  throw Error(
    'This component must have a <SideDrawerProvider/> ancestor in the same React tree.'
  );
}

const defaultState = {
  sideDrawerStack: [] as string[],
  pushToDrawerStack: notInContextFn as (drawerKey: string) => void,
  popFromDrawerStack: notInContextFn as (drawerKey: string) => void,
};
export type SideDrawerState = typeof defaultState;

const SideDrawerContext = React.createContext<SideDrawerState>(defaultState);

export const SideDrawerProvider = ({ children }: { children: ReactNode }) => {
  let [sideDrawerStack, setSideDrawerStack] = useState<string[]>([]);

  const pushToDrawerStack = useCallback((key: string) => {
    setSideDrawerStack((stack) => {
      return [...stack, key];
    });
  }, []);
  const popFromDrawerStack = useCallback((key: string) => {
    setSideDrawerStack((stack) => {
      return stack.filter((drawerKey) => {
        return drawerKey !== key;
      });
    });
  }, []);

  const context = {
    sideDrawerStack,
    pushToDrawerStack,
    popFromDrawerStack,
  };

  return (
    <SideDrawerContext.Provider value={context}>
      {children}
    </SideDrawerContext.Provider>
  );
};

// Utils
// ------------------------------
export const useSideDrawerManager = (uniqueKey: string) => {
  let sideDrawerState = React.useContext(SideDrawerContext);
  // Stable (useCallback in the provider), unlike the context value itself.
  const pushToDrawerStack = sideDrawerState.pushToDrawerStack;
  const popFromDrawerStack = sideDrawerState.popFromDrawerStack;

  // keep the stack in sync on mount/unmount
  useEffect(() => {
    pushToDrawerStack(uniqueKey);
    return () => {
      popFromDrawerStack(uniqueKey);
    };
  }, [pushToDrawerStack, popFromDrawerStack, uniqueKey]);
  // the last key in the array is the "top" drawer visually, so the depth is the inverse index
  // be careful not to mutate the stack
  let depth = sideDrawerState.sideDrawerStack
    .slice()
    .reverse()
    .indexOf(uniqueKey);
  // if it's not in the stack already,
  // we know that it should be the last drawer in the stack but the effect hasn't happened yet
  // so we need to make the depth 0 so the depth is correct even though the effect hasn't happened yet
  return depth === -1 ? 0 : depth;
};
